import { useLocalUserSettings } from '#pplx/frontend/apps/ask/user-settings/getLocalUserSettings.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { SearchModel, SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { EMPTY_OBJECT } from '#pplx/frontend/common/defaults.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
const PREFERRED_SEARCH_MODELS_KEY = 'preferredSearchModels-v1';
const loggedInvalidPreferredSearchModels = new Set();
const LEGACY_ASI_OPUS_THINKING_MODEL = 'pplx_asi_opus_thinking';
const LEGACY_ASI_SONNET_THINKING_MODEL = 'pplx_asi_sonnet_thinking';
const normalizePreferredSearchModel = (model) => {
    switch (model) {
        case SearchModel.ASI:
            return SearchModel.ASI_OPUS;
        case LEGACY_ASI_OPUS_THINKING_MODEL:
            return SearchModel.ASI_OPUS;
        case LEGACY_ASI_SONNET_THINKING_MODEL:
            return SearchModel.ASI_SONNET;
        default:
            return model;
    }
};
function normalizePreferredSearchModels(models) {
    let hasChanges = false;
    const normalizedModels = {};
    for (const [mode, model] of Object.entries(models)) {
        const normalizedModel = normalizePreferredSearchModel(model);
        normalizedModels[mode] = normalizedModel;
        hasChanges ||= normalizedModel !== model;
    }
    return {
        models: hasChanges ? normalizedModels : models,
        hasChanges,
    };
}
function validateStoredPreferredSearchModels(models, getModelConfig, getModelMode) {
    const validation = Object.entries(models).reduce((result, [mode, storedModel]) => {
        const searchMode = mode;
        const model = normalizePreferredSearchModel(storedModel);
        const resolvedMode = getModelMode(model);
        const isConfigured = getModelConfig(model) !== undefined;
        const hasModeMismatch = resolvedMode !== searchMode;
        if (!isConfigured || hasModeMismatch) {
            logInvalidPreferredSearchModel({
                model: storedModel,
                normalizedModel: model,
                requestedMode: searchMode,
                resolvedMode,
                reason: !isConfigured ? 'invalid-model' : 'mode-mismatch',
            });
            return { ...result, hasChanges: true };
        }
        return {
            models: { ...result.models, [searchMode]: model },
            hasChanges: result.hasChanges || model !== storedModel,
        };
    }, { models: {}, hasChanges: false });
    return validation.hasChanges ? validation : { models, hasChanges: false };
}
function logInvalidPreferredSearchModel({ model, normalizedModel, requestedMode, resolvedMode, reason, }) {
    const logKey = `${reason}:${requestedMode}:${model}:${normalizedModel ?? ''}:${resolvedMode ?? ''}`;
    if (loggedInvalidPreferredSearchModels.has(logKey)) {
        return;
    }
    loggedInvalidPreferredSearchModels.add(logKey);
    logger.warn('[usePreferredSearchModels] Invalid preferred search model', {
        model,
        normalizedModel,
        requestedMode,
        resolvedMode,
        reason,
    });
}
export function usePreferredSearchModels() {
    const { getModelConfig, getModelMode, hasLoadedModelConfigFromServer } = useSearchModels({
        reason: 'use-preferred-search-models',
    });
    const canValidateStoredPreferences = hasLoadedModelConfigFromServer;
    const [preferredSearchModelsExternal, setPreferredSearchModelsExternal, getPreferredSearchModelsExternal,] = useLocalUserSettings(PREFERRED_SEARCH_MODELS_KEY, EMPTY_OBJECT);
    const [preferredSearchModelsInternal, setPreferredSearchModelsInternal] = useState(() => normalizePreferredSearchModels(preferredSearchModelsExternal).models);
    // Sync internal state when external storage changes (e.g. from another hook instance via broadcast).
    // Only persist unconfigured or mode-invalid removals after model config loads from server.
    useEffect(() => {
        const normalized = normalizePreferredSearchModels(preferredSearchModelsExternal);
        const validated = canValidateStoredPreferences
            ? validateStoredPreferredSearchModels(normalized.models, getModelConfig, getModelMode)
            : normalized;
        setPreferredSearchModelsInternal(validated.models);
        if (normalized.hasChanges || validated.hasChanges) {
            setPreferredSearchModelsExternal(validated.models);
        }
    }, [
        getModelConfig,
        getModelMode,
        canValidateStoredPreferences,
        preferredSearchModelsExternal,
        setPreferredSearchModelsExternal,
    ]);
    const setPreferredSearchModels = useCallback((updater) => {
        const normalizeUpdater = (oldModels) => {
            const newModels = typeof updater === 'function' ? updater(oldModels) : updater;
            return normalizePreferredSearchModels(newModels).models;
        };
        setPreferredSearchModelsExternal(normalizeUpdater);
        setPreferredSearchModelsInternal(normalizeUpdater);
    }, [setPreferredSearchModelsExternal]);
    const setPreferredSearchModel = useCallback((searchMode, searchModel) => {
        setPreferredSearchModels((oldPreferredSearchModels) => {
            const newPreferredSearchModels = {
                ...oldPreferredSearchModels,
            };
            if (searchModel) {
                newPreferredSearchModels[searchMode] = searchModel;
            }
            else if (searchMode) {
                delete newPreferredSearchModels[searchMode];
            }
            return newPreferredSearchModels;
        });
    }, [setPreferredSearchModels]);
    const clearPreferredSearchModel = useCallback((searchMode) => {
        setPreferredSearchModel(searchMode, undefined);
    }, [setPreferredSearchModel]);
    const getPreferredSearchModel = useCallback((searchMode) => {
        const model = preferredSearchModelsInternal[searchMode];
        if (!model) {
            return undefined;
        }
        const normalizedModel = normalizePreferredSearchModel(model);
        if (canValidateStoredPreferences) {
            const resolvedMode = getModelMode(normalizedModel);
            const isConfigured = getModelConfig(normalizedModel) !== undefined;
            if (!isConfigured || resolvedMode !== searchMode) {
                logInvalidPreferredSearchModel({
                    model,
                    normalizedModel,
                    requestedMode: searchMode,
                    resolvedMode,
                    reason: !isConfigured ? 'invalid-model' : 'mode-mismatch',
                });
                return undefined;
            }
        }
        return normalizedModel;
    }, [canValidateStoredPreferences, getModelConfig, getModelMode, preferredSearchModelsInternal]);
    // On visibility change, sync the external storage value to internal state.
    useEffect(() => {
        function handleVisibilityChange() {
            if (document.visibilityState !== 'visible') {
                return;
            }
            const storedValue = getPreferredSearchModelsExternal();
            if (!canValidateStoredPreferences) {
                return;
            }
            const validated = validateStoredPreferredSearchModels(storedValue, getModelConfig, getModelMode);
            setPreferredSearchModelsInternal(validated.models);
            if (validated.hasChanges) {
                setPreferredSearchModelsExternal(validated.models);
            }
        }
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [
        canValidateStoredPreferences,
        getModelConfig,
        getModelMode,
        getPreferredSearchModelsExternal,
        setPreferredSearchModelsExternal,
    ]);
    return useMemo(() => ({
        setPreferredSearchModel,
        clearPreferredSearchModel,
        getPreferredSearchModel,
    }), [setPreferredSearchModel, clearPreferredSearchModel, getPreferredSearchModel]);
}
//# sourceMappingURL=usePreferredSearchModels.js.map